// TanStack Query hooks for member data

import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Country, State } from "country-state-city";
import { trpcClient } from "../trpc";

// =============================================================================
// Types
// =============================================================================

/** A single field descriptor returned by the API (from batch.fields + schemas). */
export interface MemberField {
  schemaId: number;
  alias: string;
  required: boolean;
  type: string;
  choices: string[] | null;
  value: unknown;
}

export interface MemberInfo {
  id: number;
  email: string | null;
  name: string | null;
  client: {
    id: string;
    name: string;
    isTestClient: boolean;
  };
}

export interface MemberDataResponse {
  member: MemberInfo | null;
  fields: MemberField[];
  termsAccepted: boolean;
}

/** All countries with US and Canada pinned to the top. */
export const COMMON_COUNTRIES: { code: string; name: string }[] = (() => {
  const all = Country.getAllCountries().map((c) => ({
    code: c.isoCode,
    name: c.name,
  }));
  const pinned = ["US", "CA"];
  const top = pinned
    .map((code) => all.find((c) => c.code === code))
    .filter(Boolean) as { code: string; name: string }[];
  const rest = all.filter((c) => !pinned.includes(c.code));
  return [...top, ...rest];
})();

/** Normalize a country value (full name or ISO code) to its ISO code. */
export function normalizeCountryCode(value: string): string {
  if (!value) return "";
  const trimmed = value.trim();
  // Already an ISO code?
  if (COMMON_COUNTRIES.some((c) => c.code === trimmed.toUpperCase()))
    return trimmed.toUpperCase();
  // Full name → ISO code
  const match = COMMON_COUNTRIES.find(
    (c) => c.name.toLowerCase() === trimmed.toLowerCase(),
  );
  return match?.code ?? trimmed;
}

/** Return the list of provinces/states for a given country code, or an empty array. */
export function getProvincesForCountry(
  countryCode: string,
): { code: string; name: string }[] {
  const code = countryCode?.toUpperCase();
  if (!code) return [];
  return State.getStatesOfCountry(code).map((s) => ({
    code: s.isoCode,
    name: s.name,
  }));
}

// =============================================================================
// Query Options
// =============================================================================

export function memberDataQueryOptions(
  organizationId: string,
  requestId: string,
) {
  return queryOptions({
    queryKey: ["memberData", organizationId, requestId],
    queryFn: () =>
      trpcClient.memberData.getMemberData.query({ organizationId, requestId }),
    enabled: !!requestId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook to fetch member data with dynamic field configuration.
 */
export function useMemberDataQuery(organizationId: string, requestId: string) {
  return useQuery(memberDataQueryOptions(organizationId, requestId));
}

/**
 * Hook to update member fields using dynamic schema ID -> value pairs.
 * When requestId is provided, the mutation also completes the member info request.
 */
export function useUpdateMemberFieldsMutation(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      profileId: string;
      memberIds: number[];
      fields: Record<string, string | null>;
      requestId?: string;
    }) =>
      trpcClient.memberData.updateMemberFields.mutate({
        organizationId,
        profileId: input.profileId,
        memberIds: input.memberIds,
        fields: input.fields,
        requestId: input.requestId,
      }),
    onSuccess: (_data, variables) => {
      // Invalidate all memberData queries for this org (any requestId)
      queryClient.invalidateQueries({
        queryKey: ["memberData", organizationId],
      });
      // When request was completed, invalidate interactions so the task shows as completed
      if (variables.requestId) {
        queryClient.invalidateQueries({
          queryKey: ["projects", "interactions"],
        });
      }
    },
  });
}

// =============================================================================
// Address Types & Utilities
// =============================================================================

export interface MemberAddress {
  firstName: string;
  lastName: string;
  address1: string;
  address2: string;
  city: string;
  country: string;
  province: string;
  zip: string;
  phone: string;
}

/** Check if all required address fields are filled. */
export function isAddressComplete(address: MemberAddress): boolean {
  return !!(
    address.firstName &&
    address.lastName &&
    address.address1 &&
    address.city &&
    address.country &&
    address.zip &&
    address.phone
  );
}

/** Format address into a single display line. */
export function formatAddressLine(address: MemberAddress): string {
  const parts = [
    address.address1,
    address.address2,
    address.city,
    address.province,
    address.zip,
    address.country,
  ].filter(Boolean);
  return parts.join(", ");
}

/** Format first + last name. */
export function formatFullName(address: MemberAddress): string {
  return [address.firstName, address.lastName].filter(Boolean).join(" ");
}
